import { useCallback, useMemo } from 'react';
import { GTI_REGEX } from 'src/constants';

interface ValidationResult {
  gti: string;
  isValid: boolean;
  hasTitleData: boolean;
}

interface UseGtiValidationProps {
  value: string;
  gtiTitleMap?: Map<string, { title?: string; coverImageUrl?: string }>;
  isFetching?: boolean;
  requireCoverImage?: boolean;
}

interface UseGtiValidationReturn {
  allGtis: string[];
  uniqueGtis: string[];
  validGtis: string[];
  validationResults: ValidationResult[];
  gtiCount: number;
  validCount: number;
  isGtiValid: (gti: string) => boolean;
  parseGtiList: (text: string) => string[];
  parseValidGtiList: (text: string) => string[];
  validateGtiList: (gtis: string[]) => ValidationResult[];
  getFilteredValue: () => string;
}

export const useGtiValidation = ({
  value,
  gtiTitleMap,
  isFetching = false,
  requireCoverImage = true,
}: UseGtiValidationProps): UseGtiValidationReturn => {
  // Parse GTI list from textarea value
  const parseGtiList = useCallback((text: string): string[] => {
    if (!text || typeof text !== 'string') return [];
    return text
      .split(/[\n,]/)
      .map(gti => gti.trim())
      .filter(gti => gti.length > 0);
  }, []);

  // Parse and filter only valid GTIs (for API calls)
  const parseValidGtiList = useCallback((text: string): string[] => {
    if (!text || typeof text !== 'string') return [];
    return text
      .split(/[\n,]/)
      .map(gti => gti.trim())
      .filter(gti => gti.length > 0 && GTI_REGEX.test(gti));
  }, []);

  // Compute whether a GTI has title data in the map
  const computeHasTitleData = useCallback((gti: string): boolean => {
    return requireCoverImage
      ? Boolean(gtiTitleMap?.has(gti) && gtiTitleMap.get(gti)?.coverImageUrl)
      : Boolean(gtiTitleMap?.has(gti));
  }, [gtiTitleMap, requireCoverImage]);

  // Check if a GTI is fully valid (format + title data when not fetching)
  const isGtiValid = useCallback((gti: string): boolean => {
    const isValidFormat = GTI_REGEX.test(gti);
    return isValidFormat && (isFetching ? true : computeHasTitleData(gti));
  }, [computeHasTitleData, isFetching]);

  // Validate GTI list
  const validateGtiList = useCallback((gtis: string[]): ValidationResult[] => {
    return gtis.map(gti => ({
      gti,
      isValid: GTI_REGEX.test(gti),
      hasTitleData: computeHasTitleData(gti),
    }));
  }, [computeHasTitleData]);

  // Computed values
  const allGtis = useMemo(() => parseGtiList(String(value || '')), [parseGtiList, value]);
  const uniqueGtis = useMemo(() => Array.from(new Set(allGtis)), [allGtis]);
  const validGtis = useMemo(() => parseValidGtiList(String(value || '')), [parseValidGtiList, value]);
  const validationResults = useMemo(() => validateGtiList(uniqueGtis), [validateGtiList, uniqueGtis]);

  const gtiCount = uniqueGtis.length;
  const validCount = validationResults.filter(r => isGtiValid(r.gti)).length;

  // Get filtered value with only valid GTIs for API calls
  const getFilteredValue = useCallback(() => {
    return validGtis.join('\n');
  }, [validGtis]);

  return {
    allGtis,
    uniqueGtis,
    validGtis,
    validationResults,
    gtiCount,
    validCount,
    isGtiValid,
    parseGtiList,
    parseValidGtiList,
    validateGtiList,
    getFilteredValue,
  };
};
